import {
  aplicarCampos,
  type Campos,
  COLORES_DE_TINTA,
  type ColorDeTinta,
  formatearDia,
  formatearFechaHora,
  formatearHora,
  formatearRevision,
  NOMBRE_ESTADO,
  type Tinta,
} from "@gestiondoc/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { trpcClient, useTRPC } from "../api.ts";
import { BotonPdf } from "../componentes/BotonPdf.tsx";
import { Encabezado } from "../componentes/Encabezado.tsx";
import {
  IconoBorrador,
  IconoDedo,
  IconoDeshacer,
  IconoLapiz,
  IconoResaltador,
} from "../componentes/IconosDeTinta.tsx";
import { PersonalDelDia } from "../componentes/PersonalDelDia.tsx";
import { ResultadoDeLaHoja } from "../componentes/ResultadoDeLaHoja.tsx";
import { Aviso, Boton, BotonEnlace, EstadoVacio } from "../componentes/ui.tsx";
import { useFlujogramas } from "../editor/mermaid.ts";
import { datosDeHoja } from "../editor/VistaDeContenido.tsx";
import { VistaHoja } from "../editor/VistaHoja.tsx";
import { CapaDeTinta, type HerramientaActiva, type Pluma } from "../tinta/CapaDeTinta.tsx";
import { VistaPdf } from "../tinta/VistaPdf.tsx";
import type { HojaAbierta } from "../tipos.ts";
import { mensajeDe } from "../utiles.ts";

/**
 * Fase 3b · una hoja llena: el formato tal como se imprime, y encima se escribe con el lápiz como
 * en GoodNotes (palomitas en las casillas, hallazgos, rayas). Pensada para la tablet; se guarda
 * sola.
 */
export function PaginaHoja() {
  const { id } = useParams({ from: "/app/hojas/$id" });
  const trpc = useTRPC();
  const q = useQuery({
    ...trpc.hojas.obtener.queryOptions({ id }),
    // Mientras se llena, lo que hay en pantalla es la verdad: no se vuelve a pedir sola. Pero
    // al salir no se guarda en caché: guardar no la actualiza, y reabrir la hoja con la tinta
    // de cuando se abrió la primera vez haría que el siguiente trazo borrara lo escrito.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  if (q.isPending) return <EstadoVacio>Cargando la hoja…</EstadoVacio>;
  if (q.isError) return <EstadoVacio>{q.error.message}</EstadoVacio>;
  return <Hoja key={id} inicial={q.data} />;
}

const COLORES_LAPIZ: ColorDeTinta[] = ["negro", "azul", "rojo", "verde"];
const NOMBRE_COLOR: Record<ColorDeTinta, string> = {
  negro: "Negro",
  azul: "Azul",
  rojo: "Rojo",
  verde: "Verde",
  amarillo: "Amarillo",
};
const GROSORES = [
  { valor: 1.4, nombre: "Fino" },
  { valor: 2.2, nombre: "Medio" },
  { valor: 3.6, nombre: "Grueso" },
];
const GROSOR_RESALTADOR = 16;
const MAX_HISTORIA = 100;

function Hoja({ inicial }: { inicial: HojaAbierta }) {
  const trpc = useTRPC();
  const editable = inicial.puedeEditar;
  const [tinta, setTinta] = useState<Tinta>(inicial.tinta);
  /** Lo escrito a máquina (el personal del día): se dibuja dentro de las celdas del formato. */
  const [campos, setCampos] = useState<Campos>(inicial.campos);
  const contenido = useMemo(
    () => (inicial.contenido ? aplicarCampos(inicial.contenido, campos) : null),
    [inicial.contenido, campos],
  );
  // Deshacer y rehacer: cada cambio cambia `tinta`, que es lo que vuelve a dibujar.
  const historia = useRef<{ pasado: Tinta[]; futuro: Tinta[] }>({ pasado: [], futuro: [] });
  const [herramienta, setHerramienta] = useState<HerramientaActiva>("lapiz");
  const [color, setColor] = useState<ColorDeTinta>("azul");
  const [grosor, setGrosor] = useState(2.2);
  const [conDedo, setConDedo] = useState(false);
  const [vioLapiz, setVioLapiz] = useState(false);

  const [estado, setEstado] = useState<"guardada" | "pendiente" | "guardando" | "error">(
    "guardada",
  );
  const [guardadaEn, setGuardadaEn] = useState<Date | null>(inicial.actualizadaEn);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync } = useMutation(trpc.hojas.guardar.mutationOptions());
  const ultima = useRef(tinta);
  ultima.current = tinta;
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enVuelo = useRef(false);
  const repetir = useRef(false);
  /** La versión de la hoja que se tiene: el servidor rechaza guardar encima de otra más nueva. */
  const version = useRef(inicial.version);

  // Un guardado a la vez: si llega otro mientras tanto, se repite al terminar con lo último.
  const guardarAhora = useCallback(async () => {
    if (!editable) return;
    if (temporizador.current) clearTimeout(temporizador.current);
    temporizador.current = null;
    if (enVuelo.current) {
      repetir.current = true;
      return;
    }
    enVuelo.current = true;
    setEstado("guardando");
    try {
      const r = await mutateAsync({
        id: inicial.id,
        tinta: ultima.current,
        version: version.current,
      });
      version.current = r.version;
      setGuardadaEn(r.guardadaEn);
      setEstado("guardada");
      setError(null);
    } catch (e) {
      setEstado("error");
      setError(mensajeDe(e));
    } finally {
      enVuelo.current = false;
      if (repetir.current) {
        repetir.current = false;
        void guardarAhora();
      }
    }
  }, [editable, inicial.id, mutateAsync]);

  // Antes de pedir el PDF: lo pendiente guardado y ningún guardado en camino, para que el PDF
  // sea de lo que se ve.
  const guardarTodo = useCallback(async () => {
    await guardarAhora();
    while (enVuelo.current) await new Promise((r) => setTimeout(r, 100));
  }, [guardarAhora]);

  const programar = useCallback(() => {
    if (!editable) return;
    setEstado("pendiente");
    if (temporizador.current) clearTimeout(temporizador.current);
    temporizador.current = setTimeout(() => void guardarAhora(), 1500);
  }, [editable, guardarAhora]);

  // Al salir de la pantalla, o al cambiar de app en la tablet, se guarda lo pendiente ya.
  const guardarRef = useRef(guardarAhora);
  guardarRef.current = guardarAhora;
  useEffect(() => {
    const alOcultar = () => {
      if (document.visibilityState === "hidden" && temporizador.current) {
        void guardarRef.current();
      }
    };
    document.addEventListener("visibilitychange", alOcultar);
    return () => {
      document.removeEventListener("visibilitychange", alOcultar);
      if (temporizador.current) void guardarRef.current();
    };
  }, []);

  const cambiar = useCallback(
    (nueva: Tinta) => {
      historia.current = {
        pasado: [...historia.current.pasado, ultima.current].slice(-MAX_HISTORIA),
        futuro: [],
      };
      setTinta(nueva);
      programar();
    },
    [programar],
  );
  const deshacer = useCallback(() => {
    const h = historia.current;
    const previa = h.pasado.at(-1);
    if (!previa) return;
    historia.current = { pasado: h.pasado.slice(0, -1), futuro: [ultima.current, ...h.futuro] };
    setTinta(previa);
    programar();
  }, [programar]);
  const rehacer = useCallback(() => {
    const h = historia.current;
    const siguiente = h.futuro[0];
    if (!siguiente) return;
    historia.current = { pasado: [...h.pasado, ultima.current], futuro: h.futuro.slice(1) };
    setTinta(siguiente);
    programar();
  }, [programar]);

  useEffect(() => {
    if (!editable) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) {
        e.preventDefault();
        deshacer();
      } else if (k === "y" || (k === "z" && e.shiftKey)) {
        e.preventDefault();
        rehacer();
      } else if (k === "s") {
        e.preventDefault();
        void guardarRef.current();
      }
    };
    window.addEventListener("keydown", alTeclear);
    return () => window.removeEventListener("keydown", alTeclear);
  }, [editable, deshacer, rehacer]);

  const alVerLapiz = useCallback(() => {
    setVioLapiz(true);
    setConDedo(false);
  }, []);

  const svgs = useFlujogramas(inicial.contenido);
  const fijos = useMemo(() => new Set(inicial.fijos), [inicial.fijos]);
  const pluma: Pluma = {
    herramienta,
    color,
    grosor: herramienta === "resaltador" ? GROSOR_RESALTADOR : grosor,
  };
  const textoEstado =
    estado === "guardando"
      ? "Guardando…"
      : estado === "pendiente"
        ? "Cambios sin guardar"
        : estado === "error"
          ? "Sin guardar"
          : guardadaEn
            ? `Guardada ${formatearFechaHora(guardadaEn)}`
            : "";
  const formatoEnBorrador =
    inicial.revision.estado !== "vigente" && inicial.revision.estado !== "obsoleta";
  const capa = (
    <CapaDeTinta
      tinta={tinta}
      onCambio={cambiar}
      pluma={pluma}
      editable={editable}
      conDedo={conDedo}
      onLapiz={alVerLapiz}
      fijos={fijos}
    />
  );

  return (
    <div className="max-w-[1200px] pb-10">
      <Encabezado
        overline={`${inicial.documento.proceso.nombre} › ${inicial.documento.codigo} › Hoja del ${formatearDia(inicial.fecha)}, ${formatearHora(inicial.creadaEn)}`}
        titulo={inicial.documento.nombre}
        acciones={
          <>
            <BotonPdf
              etiqueta="PDF"
              clave={["hoja", inicial.id]}
              consultar={() => trpcClient.hojas.pdf.query({ id: inicial.id })}
              pedir={() => trpcClient.hojas.pedirPdf.mutate({ id: inicial.id })}
              inicial={inicial.pdfId}
              version={`${guardadaEn?.getTime() ?? 0}|${inicial.estado}|${inicial.resultado}`}
              antes={editable ? guardarTodo : undefined}
              onAviso={setError}
            />
            <BotonEnlace to="/diligencias">Diligencias</BotonEnlace>
            <BotonEnlace
              to="/documentos/$codigo/hojas"
              params={{ codigo: inicial.documento.codigo }}
            >
              Todas las hojas
            </BotonEnlace>
          </>
        }
      />

      {error && <Aviso className="mb-4">No se pudo guardar: {error}</Aviso>}
      {!editable && (
        <Aviso tono="ambar" className="mb-4">
          Solo lectura: la llenó {inicial.quien.nombre} el {formatearDia(inicial.fecha)}. Una hoja
          la edita quien la abrió: ese día, o después si quedó por verificar.
        </Aviso>
      )}
      {editable && inicial.esDeHoy && inicial.personalDelDia && (
        <PersonalDelDia
          hojaId={inicial.id}
          inicial={inicial.personalDelDia}
          antes={guardarTodo}
          onPuesto={(r) => {
            setCampos(r.campos);
            setGuardadaEn(r.guardadaEn);
          }}
        />
      )}
      <ResultadoDeLaHoja hoja={inicial} antes={editable ? guardarTodo : undefined} />
      {editable && formatoEnBorrador && (
        <Aviso tono="ambar" className="mb-4">
          El formato está en {NOMBRE_ESTADO[inicial.revision.estado].toLowerCase()}: esta hoja se
          llena sobre la Rev. {formatearRevision(inicial.revision.numero)}. Si el formato cambia
          antes de aprobarse, lo escrito puede quedar corrido.
        </Aviso>
      )}

      {editable && (
        <div className="sticky top-14 z-20 mb-3 flex flex-wrap items-center gap-2 rounded-tarjeta border border-borde bg-white/95 px-2.5 py-2 shadow-sm backdrop-blur lg:top-2">
          <GrupoDeHerramientas>
            <BotonHerramienta
              activo={herramienta === "lapiz"}
              etiqueta="Lápiz"
              onClick={() => setHerramienta("lapiz")}
            >
              <IconoLapiz />
            </BotonHerramienta>
            <BotonHerramienta
              activo={herramienta === "resaltador"}
              etiqueta="Resaltador"
              onClick={() => setHerramienta("resaltador")}
            >
              <IconoResaltador />
            </BotonHerramienta>
            <BotonHerramienta
              activo={herramienta === "borrador"}
              etiqueta="Borrador (borra trazos enteros)"
              onClick={() => setHerramienta("borrador")}
            >
              <IconoBorrador />
            </BotonHerramienta>
          </GrupoDeHerramientas>

          {herramienta === "lapiz" && (
            <>
              <GrupoDeHerramientas>
                {COLORES_LAPIZ.map((c) => (
                  <button
                    key={c}
                    type="button"
                    aria-pressed={color === c}
                    aria-label={`Color ${NOMBRE_COLOR[c].toLowerCase()}`}
                    title={NOMBRE_COLOR[c]}
                    onClick={() => setColor(c)}
                    className={`grid h-11 w-11 place-items-center rounded-lg ${color === c ? "bg-fondo ring-2 ring-marino" : ""}`}
                  >
                    <span
                      className="h-6 w-6 rounded-full border border-black/10"
                      style={{ background: COLORES_DE_TINTA[c] }}
                    />
                  </button>
                ))}
              </GrupoDeHerramientas>
              <GrupoDeHerramientas>
                {GROSORES.map((g) => (
                  <button
                    key={g.valor}
                    type="button"
                    aria-pressed={grosor === g.valor}
                    aria-label={`Trazo ${g.nombre.toLowerCase()}`}
                    title={g.nombre}
                    onClick={() => setGrosor(g.valor)}
                    className={`grid h-11 w-11 place-items-center rounded-lg ${grosor === g.valor ? "bg-fondo ring-2 ring-marino" : ""}`}
                  >
                    <span
                      className="rounded-full"
                      style={{
                        width: g.valor * 3.2,
                        height: g.valor * 3.2,
                        background: COLORES_DE_TINTA[color],
                      }}
                    />
                  </button>
                ))}
              </GrupoDeHerramientas>
            </>
          )}

          <GrupoDeHerramientas>
            <BotonHerramienta
              etiqueta="Deshacer (Ctrl+Z)"
              onClick={deshacer}
              disabled={!historia.current.pasado.length}
            >
              <IconoDeshacer />
            </BotonHerramienta>
            <BotonHerramienta
              etiqueta="Rehacer (Ctrl+Y)"
              onClick={rehacer}
              disabled={!historia.current.futuro.length}
            >
              <IconoDeshacer espejo />
            </BotonHerramienta>
          </GrupoDeHerramientas>

          {!vioLapiz && (
            <BotonHerramienta
              activo={conDedo}
              etiqueta={
                conDedo
                  ? "El dedo escribe. Toca para que el dedo desplace la hoja"
                  : "El dedo desplaza la hoja. Toca para escribir con el dedo"
              }
              onClick={() => setConDedo((v) => !v)}
            >
              <IconoDedo />
            </BotonHerramienta>
          )}

          <div className="ml-auto flex items-center gap-2 pl-1">
            <span
              className={`text-[12px] font-semibold ${
                estado === "error"
                  ? "text-rojo"
                  : estado === "guardada"
                    ? "text-verde"
                    : "text-texto-3"
              }`}
              aria-live="polite"
            >
              {textoEstado}
            </span>
            <Boton onClick={() => void guardarAhora()} disabled={estado === "guardando"}>
              Guardar
            </Boton>
          </div>
        </div>
      )}

      <div className="rounded-tarjeta bg-gris-fondo p-2 sm:p-4">
        {contenido ? (
          <VistaHoja
            // Una hoja llena no lleva el pie de firmas del formato: nadie la firma, la evidencia
            // es quién la llenó y cuándo.
            datos={{ ...datosDeHoja(inicial, contenido), firmas: [], svgs }}
            encima={capa}
          />
        ) : inicial.pdfFormatoId ? (
          // Un formato cargado como archivo: se escribe sobre las páginas de su PDF.
          <VistaPdf url={`/archivos/${inicial.pdfFormatoId}?ver=1`} encima={capa} />
        ) : (
          <EstadoVacio>El formato de esta hoja no tiene contenido para mostrar.</EstadoVacio>
        )}
      </div>
      {editable && (
        <p className="mt-2 text-center text-[12px] text-texto-3">
          {vioLapiz
            ? "El lápiz escribe y el dedo desplaza la hoja; la palma apoyada no raya."
            : "Escribe con el lápiz o el ratón. El dedo desplaza la hoja, salvo que actives la mano."}
        </p>
      )}
    </div>
  );
}

/** Un grupo de botones de la barra, en su propio recuadro. */
function GrupoDeHerramientas({ children }: { children: ReactNode }) {
  return (
    <div className="flex items-center gap-0.5 rounded-xl border border-borde/70 bg-white p-0.5">
      {children}
    </div>
  );
}

function BotonHerramienta({
  activo = false,
  etiqueta,
  children,
  ...resto
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { activo?: boolean; etiqueta: string }) {
  return (
    <button
      type="button"
      aria-label={etiqueta}
      title={etiqueta}
      aria-pressed={activo}
      {...resto}
      className={`grid h-11 w-11 place-items-center rounded-lg disabled:opacity-30 ${
        activo ? "bg-marino text-white" : "text-marino hover:bg-fondo"
      }`}
    >
      {children}
    </button>
  );
}
